// NEW_FILE_JUSTIFIED: Orchestrator component for template-driven multi-step pre-auth application wizard — mirrors ClaimApplicationWizard but for pre-authorizations

import { useMemo, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Save, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { StepProgress } from '@/components/insurance/StepProgress';
import { ClaimFormSectionCard } from '@/components/insurance/claim-application/ClaimFormSectionCard';
import { ReviewStep } from '@/components/insurance/policy-application/ReviewStep';
import { FieldRenderer } from '@/components/insurance/policy-application/FieldRenderer';
import { usePreAuthApplicationWizard } from '@/hooks/usePreAuthApplicationWizard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: component in src/components/insurance/preauth-application/ needs convex api
import type { IPreAuthApplicationWizardProps, IClaimPrefillDependant } from '@/@types/claims';
import type {
  IFormStep,
  IFormField,
  IRepeatableGroupEntry,
  IConditionalDetailValue,
  IReviewSectionItem,
} from '@/@types/policyFormTemplates';

const GRID_COLS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

function groupFieldsBySections(fields: IFormField[]) {
  const sections: Array<{
    name: string | null;
    icon: string | null;
    columns: number;
    description: string | null;
    fields: IFormField[];
  }> = [];
  let currentSection: string | null = null;

  for (const field of fields) {
    const sectionName = field.section ?? null;
    if (sectionName !== currentSection || sections.length === 0) {
      sections.push({
        name: sectionName,
        icon: field.sectionIcon ?? null,
        columns: field.sectionColumns ?? 2,
        description: field.sectionDescription ?? null,
        fields: [field],
      });
      currentSection = sectionName;
    } else {
      sections[sections.length - 1].fields.push(field);
    }
  }

  return sections;
}

function isRequiredFieldMissing(field: IFormField, value: unknown): boolean {
  if (!field.required) return false;
  switch (field.fieldType) {
    case 'text':
    case 'email':
    case 'phone':
    case 'textarea':
    case 'select':
    case 'date':
      return !value || String(value).trim() === '';
    case 'number':
      return value === undefined || value === null || value === '';
    case 'boolean':
      return value === undefined || value === null;
    case 'checkbox':
      return value !== true;
    case 'address': {
      if (!value || typeof value !== 'object') return true;
      const a = value as Record<string, unknown>; // AS_JUSTIFIED: narrowed by object check; reading well-known address fields
      return !String(a['street'] ?? '').trim() || !String(a['city'] ?? '').trim();
    }
    case 'conditional_detail': {
      if (!value || typeof value !== 'object') return true;
      const cd = value as Record<string, unknown>; // AS_JUSTIFIED: narrowed by object check; reading 'answer' key only
      return cd['answer'] === undefined || cd['answer'] === null;
    }
    case 'repeatable_group': {
      const min = field.minEntries ?? 0;
      return min > 0 && (!Array.isArray(value) || value.length < min);
    }
    default:
      return !value;
  }
}

function countMissingRequired(step: IFormStep, values: Record<string, unknown>): number {
  let count = 0;
  for (const field of step.fields) {
    if (isRequiredFieldMissing(field, values[field.key])) {
      count++;
      continue;
    }
    if (field.fieldType === 'repeatable_group' && field.groupFields) {
      const entries = Array.isArray(values[field.key])
        ? (values[field.key] as IRepeatableGroupEntry[]) // AS_JUSTIFIED: narrowed by Array.isArray; repeatable_group values are IRepeatableGroupEntry[]
        : [];
      for (const entry of entries) {
        for (const sub of field.groupFields) {
          if (isRequiredFieldMissing(sub, entry.values[sub.key])) count++;
        }
      }
    }
    if (field.fieldType === 'conditional_detail' && field.subFields) {
      const cdRaw = values[field.key];
      if (cdRaw && typeof cdRaw === 'object' && 'answer' in cdRaw) {
        const cd = cdRaw as IConditionalDetailValue; // AS_JUSTIFIED: runtime narrowed by 'answer' key check
        if (cd.answer === true) {
          for (const sub of field.subFields) {
            if (isRequiredFieldMissing(sub, cd.subFieldValues?.[sub.key])) count++;
          }
        }
      }
    }
  }
  return count;
}

export function PreAuthApplicationWizard({
  templateSteps,
  templateId,
  templateName: _templateName,
  orgId,
  policyId,
  existingPreAuthId,
  existingData,
  claimantType,
  claimantId,
  onBack,
  onComplete,
  onRegisterSaveDraft,
}: IPreAuthApplicationWizardProps) {
  const {
    currentStep,
    values,
    goToStep,
    nextStep,
    prevStep,
    updateValue,
    saveDraft,
    submitPreAuth,
    isSaving,
    isSubmitting,
  } = usePreAuthApplicationWizard({
    templateSteps,
    templateId,
    orgId,
    policyId,
    existingPreAuthId,
    existingData,
    claimantType,
    claimantId,
  });

  // Register saveDraft function so parent (header button) can call it
  useEffect(() => {
    onRegisterSaveDraft?.(saveDraft);
  }, [saveDraft, onRegisterSaveDraft]);

  // ── Auto-fill from policy/member data ──────────────────────────────────────
  const prefillData = useQuery(
    api.insurance.preAuthorizations.getPreAuthPrefillData,
    policyId && claimantType && claimantId ? { policyId, claimantType, claimantId } : 'skip'
  );

  const prefillDoneRef = useRef(false);
  useEffect(() => {
    if (prefillDoneRef.current) return;
    if (!prefillData) return;
    // Don't prefill if resuming a draft with existing data
    if (existingData && Object.keys(existingData.values).length > 0) {
      prefillDoneRef.current = true;
      return;
    }

    const { insured } = prefillData;
    const prefillMap: Record<string, unknown> = {
      patient_fullName: `${insured.firstName} ${insured.lastName}`,
      patient_policyHolder: `${insured.firstName} ${insured.lastName}`,
      patient_dateOfBirth: insured.dateOfBirth,
      patient_email: insured.email,
      patient_telephone: insured.phone,
      patient_membershipId: insured.memberId,
      patient_address: insured.address,
    };

    for (const [key, value] of Object.entries(prefillMap)) {
      if (value !== undefined && value !== '' && !values[key]) {
        updateValue(key, value);
      }
    }
    prefillDoneRef.current = true;
  }, [prefillData, existingData, values, updateValue]);

  // ── Patient step: person selection (self / dependant) ─────────────────────
  const patientSelection = values['_patientSelection'] as string | undefined; // AS_JUSTIFIED: values is Record<string, unknown>; _patientSelection is always string or absent

  const handlePatientSelection = (selection: string) => {
    updateValue('_patientSelection', selection);

    if (selection === 'self') {
      if (!prefillData) return;
      updateValue(
        'patient_fullName',
        `${prefillData.insured.firstName} ${prefillData.insured.lastName}`
      );
      updateValue('patient_dateOfBirth', prefillData.insured.dateOfBirth ?? '');
      updateValue('patient_membershipId', prefillData.insured.memberId ?? '');
      updateValue('patient_telephone', prefillData.insured.phone ?? '');
      updateValue('patient_email', prefillData.insured.email ?? '');
      updateValue('patient_address', prefillData.insured.address);
    } else {
      // Dependant selected — find by _id
      const dep = prefillData?.dependants.find((d: IClaimPrefillDependant) => d._id === selection);
      if (dep) {
        updateValue('patient_fullName', dep.name);
        updateValue('patient_dateOfBirth', dep.dateOfBirth ?? '');
        updateValue('patient_membershipId', dep.memberId ?? '');
      }
    }
  };

  const progressSteps = useMemo(
    () => templateSteps.map(s => ({ title: s.title })),
    [templateSteps]
  );

  const missingByStep = useMemo(
    () => templateSteps.map(step => countMissingRequired(step, values)),
    [templateSteps, values]
  );

  const errorStepIndices = useMemo(
    () => missingByStep.flatMap((n, i) => (n > 0 ? [i] : [])),
    [missingByStep]
  );

  const currentTemplateStep = templateSteps[currentStep];
  const isLastStep = currentStep === templateSteps.length - 1;
  const isFirstStep = currentStep === 0;
  const isReviewStep = isLastStep && currentTemplateStep?.fields.length === 0;
  const nextStepTitle = !isLastStep ? templateSteps[currentStep + 1]?.title : null;

  const reviewSections = useMemo((): IReviewSectionItem[] => {
    if (!isReviewStep) return [];
    return templateSteps
      .filter(step => step.fields.length > 0)
      .map((step, i) => {
        const missing = missingByStep[i] ?? 0;
        const filledCount = step.fields.filter(f => values[f.key] !== undefined).length;
        const summary =
          missing > 0
            ? `${filledCount} of ${step.fields.length} fields completed`
            : `${step.fields.length} fields completed`;
        return {
          key: step.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: step.title,
          summary,
          icon: 'step',
          stepIndex: i,
          hasError: missing > 0,
          errorMessage:
            missing > 0
              ? `${missing} required field${missing !== 1 ? 's' : ''} missing`
              : undefined,
        };
      });
  }, [isReviewStep, templateSteps, missingByStep, values]);

  const handleSubmit = async () => {
    await submitPreAuth();
    onComplete?.();
  };

  const handleBack = () => {
    if (isFirstStep) {
      onBack?.();
    } else {
      prevStep();
    }
  };

  if (!currentTemplateStep) return null;

  const sections = !isReviewStep ? groupFieldsBySections(currentTemplateStep.fields) : [];

  return (
    <div className="flex flex-col min-h-0">
      <StepProgress
        steps={progressSteps}
        currentStep={currentStep}
        onStepClick={goToStep}
        errorSteps={isReviewStep ? errorStepIndices : undefined}
        showAllLabels={true}
        fullWidth={true}
      />

      <div className="flex-1 overflow-y-auto px-8 py-6 space-y-5 bg-background-secondary">
        {/* Review step — validation summary with Edit/Fix Issues per section */}
        {isReviewStep ? (
          <ReviewStep
            sections={reviewSections}
            onEditSection={goToStep}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        ) : (
          <>
            {/* Instruction banner on step 1 */}
            {isFirstStep && (
              <div className="flex gap-3 rounded-xl border border-[#F59E0B40] bg-[#FFFBEB] p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FEF3C7]">
                  <Info className="h-5 w-5 text-[#D97706]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#92400E]">INSTRUCTIONS</p>
                  <ul className="mt-1 text-[13px] text-[#92400E] leading-relaxed list-disc pl-4 space-y-1">
                    <li>
                      Medical Emergency Authorizations must be received within 48 hours after the
                      admission or procedure.
                    </li>
                    <li>Please attach all relevant medical records to support this request.</li>
                  </ul>
                </div>
              </div>
            )}

            {sections.map((section, sIdx) => {
              const gridClass = GRID_COLS_CLASS[section.columns] ?? 'md:grid-cols-2';

              const fieldNodes = section.fields.map(field => {
                // Replace patient_sameAsInsured toggle with dependant dropdown
                if (field.key === 'patient_sameAsInsured' && prefillData) {
                  return (
                    <div key={field.key} className="col-span-full">
                      <Label htmlFor="patient-selection-trigger" className="text-sm font-medium">
                        This request is for
                      </Label>
                      <Select value={patientSelection ?? ''} onValueChange={handlePatientSelection}>
                        <SelectTrigger id="patient-selection-trigger" className="mt-1.5 w-full">
                          <SelectValue placeholder="Select person..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self">
                            Self ({prefillData.insured.firstName} {prefillData.insured.lastName})
                          </SelectItem>
                          {prefillData.dependants.map((dep: IClaimPrefillDependant) => (
                            <SelectItem key={dep._id} value={dep._id}>
                              {dep.name} —{' '}
                              {dep.relationship.charAt(0).toUpperCase() + dep.relationship.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                }

                const isFullWidth = [
                  'textarea',
                  'address',
                  'repeatable_group',
                  'conditional_detail',
                  'checkbox',
                ].includes(field.fieldType);
                return (
                  <div key={field.key} className={isFullWidth ? 'col-span-full' : undefined}>
                    <FieldRenderer
                      field={field}
                      value={values[field.key]}
                      onChange={updateValue}
                      allValues={values}
                    />
                  </div>
                );
              });

              if (section.name === null) {
                return (
                  <div key={sIdx} className={cn('grid gap-x-5 gap-y-4', gridClass)}>
                    {fieldNodes}
                  </div>
                );
              }

              return (
                <ClaimFormSectionCard
                  key={section.name}
                  title={section.name}
                  icon={section.icon ?? undefined}
                  description={section.description ?? undefined}
                >
                  <div className={cn('grid gap-x-5 gap-y-4', gridClass)}>{fieldNodes}</div>
                </ClaimFormSectionCard>
              );
            })}

            {/* Navigation footer */}
            <div className="flex items-center justify-between pt-3 pb-6">
              {!isFirstStep ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="gap-2 rounded-lg border-border px-6"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              ) : (
                <div />
              )}

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={saveDraft}
                  disabled={isSaving}
                  className="gap-2 rounded-lg border-border px-6"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save Draft'}
                </Button>

                <Button
                  type="button"
                  onClick={nextStep}
                  className="gap-2 rounded-lg bg-[#dff8be] px-6 text-foreground hover:bg-[#d4f4a0]"
                >
                  {nextStepTitle ? `Next: ${nextStepTitle}` : 'Next'}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
